/**
 * @file Logger.ts
 * @role Minimal logging abstraction so runtime services do not depend directly on console.
 *       Tests use the noop logger by default; app wiring can opt into console output.
 * @owns Logger contract and built-in sinks.
 * @reads Nothing.
 * @neverDoes Persist logs. Send network telemetry. Throw on logging failure.
 * @invariants 17 (diagnostics never affect gameplay).
 * @see docs/CODING_STANDARDS.md
 */
export interface Logger {
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
}

export const noopLogger: Logger = {
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};

export interface ConsoleLike {
  info(message?: unknown, ...args: unknown[]): void;
  warn(message?: unknown, ...args: unknown[]): void;
  error(message?: unknown, ...args: unknown[]): void;
}

export class ConsoleLogger implements Logger {
  constructor(private readonly sink: ConsoleLike = console) {}

  info(message: string, ...args: unknown[]): void {
    this.sink.info(message, ...args);
  }

  warn(message: string, ...args: unknown[]): void {
    this.sink.warn(message, ...args);
  }

  error(message: string, ...args: unknown[]): void {
    this.sink.error(message, ...args);
  }
}
